// HueRing — 色相リング (conic-gradient ドーナツ) + 内接正方形SV のラッパ
// (consult 2026-06-21、カラーピッカーを CSP/Java 互換の「色相リング + 内接正方形SV」に).
//
// 構成:
//   - 外側: CSS `conic-gradient` の虹色ドーナツ。外周をドラッグして Hue を選ぶ
//     (pointer 角度 → Hue は hueRing.ts の pure helper、setPointerCapture でドラッグ追従)。
//   - 中央: 内接正方形 (= @uiw `Saturation` を children として受け取り絶対配置)。
//     一辺 = リング内径 × cos45°。横軸=Saturation・縦軸=Value。
//   - リング上の現在 Hue 位置にノブを表示。
//
// 角度⇄Hue・ジオメトリは hueRing.ts (pure, co-located test) に切り出し、本 component は
// DOM/pointer 配線とレイアウトだけを持つ。Saturation 本体 (正方形SV) は ColorPanel が
// 内接正方形枠 (本 component の children) に渡す = HueRing は色変換に関与しない。

import { useCallback, useRef } from 'react';

import {
  hueFromPointer,
  isPointerOnRing,
  normalizeHue,
  ringGeometry,
  unitVectorForHue,
} from './hueRing.js';

/** リング外径 (px)。最小ドック幅 (180px、内パディング差引き ~160px) でも収まるサイズ。 */
const RING_OUTER = 160;
/** リング太さ (px)。 */
const RING_THICKNESS = 18;

/** conic-gradient の虹色 (真上=0deg 始まり・時計回り、hueRing の角度定義と一致)。 */
const HUE_CONIC =
  'conic-gradient(from 0deg, ' +
  'hsl(0,100%,50%), hsl(60,100%,50%), hsl(120,100%,50%), ' +
  'hsl(180,100%,50%), hsl(240,100%,50%), hsl(300,100%,50%), hsl(360,100%,50%))';

export interface HueRingProps {
  /** 現在の Hue (0..360)。リングノブ位置に反映。 */
  readonly hue: number;
  /** Hue 変更 (= 外周ドラッグ / クリック)。 */
  readonly onHueChange: (hue: number) => void;
  /** 中央内接正方形に置く SV ボックス (= @uiw Saturation)。一辺 = リング内径×cos45°。 */
  readonly children: React.ReactNode;
}

/**
 * 色相リング + 内接正方形SV。リング外周のポインタ操作で Hue を選び、中央の children
 * (Saturation) で S/V を選ぶ。サイズは固定 (RING_OUTER) で、フロート(280px)/ドック(min180px)
 * 両幅にコンパクトに収まる。
 */
export const HueRing = ({ hue, onHueChange, children }: HueRingProps): React.ReactNode => {
  const g = ringGeometry(RING_OUTER, RING_THICKNESS);
  const holePct = (g.inner / g.outer) * 100;
  const holeMask = `radial-gradient(closest-side, transparent ${holePct}%, #000 ${holePct}%)`;
  const rootRef = useRef<HTMLDivElement>(null);

  // pointer 位置 (clientX/Y) → リング中心相対 → Hue。リング太さ内かどうかは問わず、
  // 角度だけで Hue を決める (CSP も外周ドラッグ中は半径を外れても角度追従する流儀)。
  const hueFromEvent = useCallback(
    (clientX: number, clientY: number): number => {
      const el = rootRef.current;
      if (!el) return hue;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      return hueFromPointer(clientX - cx, clientY - cy);
    },
    [hue],
  );

  // 現在 Hue のノブ位置 (リング中央半径上)。
  const v = unitVectorForHue(hue);
  const knobX = g.outer / 2 + v.x * g.knobRadius;
  const knobY = g.outer / 2 + v.y * g.knobRadius;

  return (
    <div
      ref={rootRef}
      role="slider"
      aria-label="色相"
      aria-valuemin={0}
      aria-valuemax={359}
      aria-valuenow={Math.round(normalizeHue(hue))}
      tabIndex={0}
      className="relative shrink-0 touch-none select-none"
      style={{ width: g.outer, height: g.outer }}
      onPointerDown={(e) => {
        // 中央の正方形 (Saturation) 上の pointer は Saturation に任せる (= リングは外周のみ)。
        // ヒット判定: 中心からの距離がリング内径/2 より外ならリング操作とみなす。
        // rect は CSS zoom 下で縮んだ見た目の px なので、内径も見た目の外径に対する比率で比べる。
        const r = e.currentTarget.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        if (!isPointerOnRing(dx, dy, r.width, g)) return; // 正方形領域 → Saturation に委譲
        e.currentTarget.setPointerCapture(e.pointerId);
        onHueChange(hueFromEvent(e.clientX, e.clientY));
      }}
      onPointerMove={(e) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        onHueChange(hueFromEvent(e.clientX, e.clientY));
      }}
      onPointerUp={(e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
      }}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 10 : 1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          onHueChange(normalizeHue(hue + step));
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          onHueChange(normalizeHue(hue - step));
        }
      }}
    >
      {/* 虹色ドーナツ: conic-gradient を mask で中抜きしてリング状にする。 */}
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-full"
        style={{
          background: HUE_CONIC,
          // 内径ぶんを中抜き (radial-gradient mask)。透明→不透明の境界 = 半径の inner/outer。
          // **px でなく closest-side 基準の % で指定する** (2026-09-29 iPad 実機 fb): 祖先の
          // CSS `zoom` (サイドバー表示サイズ、iPad 既定 72%) 下で iOS Safari は mask の px
          // 長さを縮めず、要素だけ縮むので穴が外径を超えてリングが消えていた (85% で 6px、
          // 72% で 0)。% なら要素サイズに対する比率なので zoom に依らず同じ形になる。
          WebkitMask: holeMask,
          mask: holeMask,
        }}
      />
      {/* Hue ノブ (現在位置)。 */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute rounded-full border-2 border-white shadow"
        style={{
          width: RING_THICKNESS - 4,
          height: RING_THICKNESS - 4,
          left: knobX,
          top: knobY,
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
        }}
      />
      {/* 内接正方形 (= Saturation を絶対配置)。一辺 = 内径×cos45°。 */}
      <div
        className="absolute"
        style={{
          left: g.squareInset,
          top: g.squareInset,
          width: g.square,
          height: g.square,
        }}
      >
        {children}
      </div>
    </div>
  );
};
